import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, buttonVariants } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { Stepper } from "@/components/ui/stepper";
import { Field, Input } from "@/components/ui/input";
import { LoadingState, ErrorState, PermissionDeniedState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { usePermission } from "@/hooks/use-permission";
import {
  createWallet as createWalletRequest,
  fetchWalletOptions,
  idempotencyKey,
  type WalletOptionsResponse,
} from "@/lib/api";
import type { WalletRequestStep } from "@/features/wallets/wallet-request-step";
import {
  WALLET_NAME_MAX,
  chainFamilies,
  createRequest,
  enabledRoutes,
  preferredRouteId,
  previewAssets,
  walletNameProblem,
  type ChainFamily,
  type ProviderRoute,
  type WalletNameProblem,
} from "@/features/wallets/wallet-create-model";

/**
 * Create Wallet (ALL-4839, PM review:
 * product-design/specs/03-wallet-operations/create-wallet-pm-review.md).
 *
 * Two steps. The operator chooses a name and, only when an admin has enabled
 * more than one, a chain family and provider route. Network availability is
 * derived from the chain family; the asset mapping is *previewed* read-only and
 * never chosen or sent; RiskGuard and PolicyKit apply when funds move, not here.
 * The review step is the confirmation — there is no type-to-confirm overlay.
 */

const STEPS = [{ label: "Wallet details" }, { label: "Review & create" }];

const NAME_ERRORS: Record<WalletNameProblem, string> = {
  empty: "Enter a wallet name.",
  too_long: `Use ${WALLET_NAME_MAX} characters or fewer.`,
  invalid_start: "Start with a letter or number.",
  invalid_chars: "Use letters, numbers, and . _ : - only. Spaces are not allowed.",
  double_dot: "Don't use two dots in a row.",
};

const NAME_HINT = `Letters, numbers, and . _ : - (no spaces), up to ${WALLET_NAME_MAX} characters. For example: treasury-ops-01.`;

type Failure = { message: string; reasonCode?: string; supportReference?: string; capability?: string };

function OptionCard({
  selected,
  onSelect,
  title,
  meta,
  name,
  value,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  meta: string;
  name: string;
  value: string;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-lg border bg-surface px-4 py-3 transition-colors focus-within:ring-2 focus-within:ring-primary-soft",
        selected ? "border-primary bg-selected-soft" : "border-line hover:border-primary",
      )}
    >
      <input type="radio" name={name} value={value} checked={selected} onChange={onSelect} className="mt-1 accent-primary" />
      <span className="min-w-0">
        <span className="block text-sm font-bold text-ink">{title}</span>
        <span className="mt-0.5 block text-xs text-muted">{meta}</span>
      </span>
    </label>
  );
}

function FactRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1 border-b border-line py-3 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className={cn("text-sm font-semibold text-ink sm:max-w-[60%] sm:text-right", mono && "font-mono break-all")}>{value}</dd>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-base font-bold text-ink">{children}</h2>;
}

function FamilyFact({ family }: { family: ChainFamily }) {
  return (
    <div className="rounded-lg border border-line bg-bg px-4 py-3">
      <div className="text-sm font-bold text-ink">{family.label}</div>
      <div className="mt-0.5 text-xs text-muted">
        {family.description}: {family.networks.map((n) => n.label).join(", ")}
      </div>
    </div>
  );
}

function RouteFact({ route }: { route: ProviderRoute }) {
  return (
    <div className="rounded-lg border border-line bg-bg px-4 py-3">
      <div className="text-sm font-bold text-ink">{route.name}</div>
      <div className="mt-0.5 font-mono text-xs text-muted">{route.id}</div>
    </div>
  );
}

export default function WalletRequestFlow({
  initialStep,
}: {
  initialStep: WalletRequestStep;
}) {
  const navigate = useNavigate();
  const canCreate = usePermission("wallet:create");

  const [options, setOptions] = React.useState<WalletOptionsResponse | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);
  const [step, setStep] = React.useState<1 | 2>(initialStep === 2 ? 2 : 1);
  const [name, setName] = React.useState("");
  const [nameTouched, setNameTouched] = React.useState(false);
  const [chain, setChain] = React.useState("");
  const [routeId, setRouteId] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [failure, setFailure] = React.useState<Failure | null>(null);
  // One key per distinct request: a retry of the same values replays it; any
  // edit to the request starts a new key.
  const attemptKey = React.useRef<{ fingerprint: string; key: string } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setOptions(null);
    fetchWalletOptions().then((result) => {
      if (!cancelled) setOptions(result);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const families = React.useMemo(() => (options?.ok ? chainFamilies(options.options) : []), [options]);
  const routes = React.useMemo(() => (options?.ok ? enabledRoutes(options.options) : []), [options]);

  // A single enabled family or route is a fact, not a choice: auto-select it.
  React.useEffect(() => {
    if (families.length > 0 && !families.some((f) => f.chain === chain)) setChain(families[0].chain);
  }, [families, chain]);
  const defaultRouteId = options?.ok ? options.options.defaultWalletProfileId : null;
  React.useEffect(() => {
    if (routes.length > 0 && !routes.some((r) => r.id === routeId)) {
      const preferred = preferredRouteId(routes, defaultRouteId);
      if (preferred) setRouteId(preferred);
    }
  }, [routes, routeId, defaultRouteId]);

  const header = (
    <>
      <Breadcrumb items={[{ label: "Wallets", href: "/wallets" }, { label: "Create wallet" }]} />
      <h1 className="mb-1 text-xl font-bold text-ink">Create wallet</h1>
      <p className="mb-5 text-sm text-muted">
        Alloy generates the address through your organization's provider route. No key material is shown or stored in this browser.
      </p>
    </>
  );

  if (!canCreate) {
    return (
      <div className="max-w-[720px]">
        {header}
        <PermissionDeniedState
          title="You can't create wallets"
          description="Creating a wallet needs the Wallet operator or Wallet admin role. Ask an organization admin to assign you one, then reload this page."
          action={
            <Link to="/wallets" className={buttonVariants({ variant: "secondary" })}>
              Back to wallets
            </Link>
          }
        />
      </div>
    );
  }

  if (options === null) {
    return (
      <div className="max-w-[720px]">
        {header}
        <LoadingState rows={3} />
      </div>
    );
  }

  if (!options.ok) {
    return (
      <div className="max-w-[720px]">
        {header}
        <ErrorState
          title="Couldn't load wallet options"
          description="The networks and provider routes your admins enabled could not be loaded, so a wallet can't be created right now."
          details={options.reasonCode}
          onRetry={() => setReloadToken((n) => n + 1)}
        />
        <SupportDetails reference={options.supportReference} capability={options.capability} className="mt-3" />
      </div>
    );
  }

  if (families.length === 0 || routes.length === 0) {
    return (
      <div className="max-w-[720px]">
        {header}
        <InfoBanner variant="warning" title="No provider route is enabled">
          Your organization has no {families.length === 0 ? "network" : "provider route"} enabled for wallet creation. An
          organization admin can enable one in the admin console.
        </InfoBanner>
        <Link to="/wallets" className={buttonVariants({ variant: "secondary" })}>
          Back to wallets
        </Link>
      </div>
    );
  }

  const family = families.find((f) => f.chain === chain) ?? families[0];
  const route =
    routes.find((r) => r.id === routeId) ?? routes.find((r) => r.id === preferredRouteId(routes, defaultRouteId)) ?? routes[0];
  // The asset mapping the wallet will be able to receive, read from the
  // admin-configured options. Read-only: creation sends no asset (PM review,
  // decision 1), and an empty mapping is stated, never filled in with a default.
  const assetPreview = previewAssets(options.options, family);
  const assetValue = assetPreview.length === 0
    ? "No assets enabled for this chain family yet"
    : assetPreview
        .map((a) => (family.networks.length > 1 ? `${a.symbol} · ${a.networkLabel}` : a.symbol))
        .join(", ");
  const nameProblem = walletNameProblem(name);
  const showNameError = nameTouched && nameProblem !== null;
  // Review is only reachable with a valid name (a ?step=2 deep link starts at details).
  const current: 1 | 2 = step === 2 && !nameProblem ? 2 : 1;

  const next = () => {
    setNameTouched(true);
    if (nameProblem) return;
    setFailure(null);
    setStep(2);
  };

  const submit = async () => {
    if (!canCreate || submitting || nameProblem) return;
    const request = createRequest(name, family, route.id);
    const fingerprint = JSON.stringify(request);
    if (!attemptKey.current || attemptKey.current.fingerprint !== fingerprint) {
      attemptKey.current = { fingerprint, key: idempotencyKey("wallet_create") };
    }
    setSubmitting(true);
    setFailure(null);
    const result = await createWalletRequest(request.accountAlias, {
      chain: request.chain,
      network: request.network,
      walletProfileId: request.walletProfileId,
      idempotencyKey: attemptKey.current.key,
    });
    setSubmitting(false);
    if (result.ok) {
      attemptKey.current = null;
      toast({
        variant: "success",
        title: "Wallet created",
        description: result.walletId ? `${request.accountAlias} created (${result.walletId}).` : `${request.accountAlias} was created.`,
        supportReference: result.supportReference,
      });
      navigate(result.walletId ? `/wallets/${encodeURIComponent(result.walletId)}` : "/wallets");
      return;
    }
    setFailure({
      message: result.error ?? "Wallet creation failed.",
      reasonCode: result.reasonCode,
      supportReference: result.supportReference,
      capability: result.capability,
    });
  };

  return (
    <div className="max-w-[720px]">
      {header}
      <Stepper steps={STEPS} current={current - 1} className="mb-6" />

      <div className="rounded-lg border border-line bg-surface p-4 sm:p-6">
        {current === 1 ? (
          <div className="flex flex-col gap-5">
            <SectionHeading>Wallet details</SectionHeading>
            <Field
              label="Wallet name"
              htmlFor="w-name"
              required
              hint={NAME_HINT}
              error={showNameError && nameProblem ? NAME_ERRORS[nameProblem] : undefined}
            >
              <Input
                id="w-name"
                value={name}
                maxLength={WALLET_NAME_MAX + 16}
                autoComplete="off"
                spellCheck={false}
                aria-invalid={showNameError || undefined}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setNameTouched(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    next();
                  }
                }}
                placeholder="treasury-ops-01"
              />
            </Field>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-xs font-semibold text-ink">Chain family</legend>
              {families.length === 1 ? (
                <FamilyFact family={family} />
              ) : (
                families.map((f) => (
                  <OptionCard
                    key={f.chain}
                    name="w-family"
                    value={f.chain}
                    selected={f.chain === family.chain}
                    onSelect={() => setChain(f.chain)}
                    title={f.label}
                    meta={`${f.description}: ${f.networks.map((n) => n.label).join(", ")}`}
                  />
                ))
              )}
              <span className="text-xs text-muted">
                Networks come from your admin's configuration. You don't pick one per wallet.
              </span>
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-xs font-semibold text-ink">Provider route</legend>
              {routes.length === 1 ? (
                <RouteFact route={route} />
              ) : (
                routes.map((r) => (
                  <OptionCard
                    key={r.id}
                    name="w-route"
                    value={r.id}
                    selected={r.id === route.id}
                    onSelect={() => setRouteId(r.id)}
                    title={r.name}
                    meta={r.id === defaultRouteId ? `${r.id} · Admin default` : r.id}
                  />
                ))
              )}
              <span className="text-xs text-muted">
                {routes.length === 1 ? "The only route your admins enabled." : "Only routes your admins enabled are listed."}
              </span>
            </fieldset>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <SectionHeading>Review & create</SectionHeading>
            <dl className="rounded-lg border border-line px-4">
              <FactRow label="Wallet name" value={name.trim()} mono />
              <FactRow label="Chain family" value={family.label} />
              <FactRow label="Available on" value={family.networks.map((n) => n.label).join(", ")} />
              <FactRow label="Assets you can receive" value={assetValue} />
              <FactRow label="Provider route" value={route.name} />
              <FactRow label="Address" value="Generated when you create the wallet" />
            </dl>
            <InfoBanner variant="info" title="What happens next" className="mb-0">
              VaultCore binds a signer and your provider route generates the address. Nothing is funded or moved. When
              funds move later, PolicyKit and RiskGuard evaluate each transfer.
            </InfoBanner>
            {failure && (
              <div role="alert">
                <InfoBanner variant="danger" title="Wallet creation failed" className="mb-0">
                  {failure.message}
                  {failure.reasonCode && failure.reasonCode !== failure.message && (
                    <span className="mt-1 block font-mono text-xs">{failure.reasonCode}</span>
                  )}
                </InfoBanner>
                <SupportDetails reference={failure.supportReference} capability={failure.capability} />
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          {current === 1 ? (
            <Link to="/wallets" className={buttonVariants({ variant: "secondary" })}>
              Cancel
            </Link>
          ) : (
            <Button variant="secondary" onClick={() => setStep(1)} disabled={submitting}>
              Back
            </Button>
          )}
          {current === 1 ? (
            <Button variant="primary" onClick={next}>
              Continue
            </Button>
          ) : (
            <Button variant="primary" onClick={submit} loading={submitting} disabled={submitting}>
              {submitting ? "Creating wallet…" : failure ? "Try again" : "Create wallet"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
